import { marked } from 'marked';
import { memo, useMemo } from 'react';
import Markdown from 'react-markdown';

import { markdownComponents, remarkPlugins } from './ChatMarkdown';

/**
 * Parse markdown into block-level tokens using marked's lexer.
 * Returns an array of raw markdown strings, one per block.
 */
function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map((token) => token.raw);
}

/**
 * A single markdown block wrapped in React.memo. Only re-renders when
 * the raw markdown string for this specific block changes.
 */
const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string }) => {
    return (
      <Markdown remarkPlugins={remarkPlugins} components={markdownComponents}>
        {content}
      </Markdown>
    );
  },
  (prevProps, nextProps) => prevProps.content === nextProps.content,
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

/**
 * Memoized markdown renderer that splits content into block-level tokens
 * and only re-renders the blocks that changed. During streaming, only the
 * last block (receiving new tokens) re-renders while completed blocks
 * remain cached.
 *
 * Based on the AI SDK memoization recipe:
 * https://sdk.vercel.ai/docs/ai-sdk-ui/chatbot#markdown-chatbot-with-memoization
 */
export const MemoizedMarkdown = memo(
  ({ content, id }: { content: string; id: string }) => {
    const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

    return (
      <>
        {blocks.map((block, index) => (
          <MemoizedMarkdownBlock content={block} key={`${id}-block-${index}`} />
        ))}
      </>
    );
  },
);

MemoizedMarkdown.displayName = 'MemoizedMarkdown';
